import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Activity } from '@docma-mm-core/activity/activity';
import { Attachment } from '@docma-mm-core/attachment/attachment';
import { AttachmentService } from '@docma-mm-core/attachment/attachment.service';
import { ChangeStatusEnum } from '@docma-mm-core/model/change-state/change-status.enum';
import { LoadState } from '@docma-mm-core/model/load-state/load-state';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { ConnectivityService } from '@mydocma/core';
import { firstValueFrom, Subject } from 'rxjs';
import { Fault, ViewFault } from '../fault';
import { FaultPersistenceService } from '../fault-persistence.service';
import { CapacitorAppSettingsService } from '../../../capacitor-app-settings/Services/capacitor-app-settings.service';
import { BlockingDialogService } from '../../../mm-faults/blocking-dialog.service';
import { MmTranslateService } from '../../../mm-translate.service';
import { AttachmentLoadingTypeEnum } from '@docma-mm-core/attachment/attachment-loading-type.enum';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';
import { MmFaultsLoaderService } from '../../../mm-faults/mm-faults-loader.service';
import { filter, take, takeUntil, toArray } from 'rxjs/operators';
import { TaskService, TaskViewService } from '@mydocma/task';
import { AttachmentDownloadJob } from './attachment-download.job';
import { AttachmentDownloadService } from './attachment-download.service';
import { IOfflineService } from '../../../mm-offline/interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../../../mm-offline/interfaces/offline-service.token';

export interface FileDownloadJobDef {
  id: string;



}

@Injectable({
  providedIn: 'root'
})
export class FaultDownloadService implements OnDestroy {

  private faultDownloadedSubject = new Subject<Fault>();
  private faultDownloadClearedSubject = new Subject<Fault>();
  private faultErrorSubject = new Subject<{ fault: Fault, error }>();

  onFaultDownloaded = this.faultDownloadedSubject.asObservable();
  onFaultDownloadCleared = this.faultDownloadClearedSubject.asObservable();

  private downloadCanceled = false;
  private onDownloadCanceled = new Subject()



  constructor(private readonly persistenceService: FaultPersistenceService,
    private readonly connectivityService: ConnectivityService,
    private attachmentService: AttachmentService,
    private capacitorAppSettingsService: CapacitorAppSettingsService,
    private blockingDialogService: BlockingDialogService,
    private translateService: MmTranslateService,
    private notificationService: NotificationService,
    private platformModeStateService: PlatformModeStateService,
    private readonly faultsLoaderService: MmFaultsLoaderService,
    private readonly attachmentDownloadService: AttachmentDownloadService,
    @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService) {



  }

  ngOnDestroy(): void {

  }

  private async loadFaults(faults: Fault[]): Promise<Fault[]> {
    //TODO: We need another implementation for faultsLoaderService.loadFaults

    this.faultsLoaderService.loadFaults(faults as ViewFault[], true, 59, this.onDownloadCanceled);

    // we waiting until all faults have been loaded loaded
    await firstValueFrom(this.faultsLoaderService.onFaultLoaded
      .pipe(
        takeUntil(this.onDownloadCanceled),
        filter(f => faults.some(sf => sf.uid === f.uid)),
        take(faults.length),
        toArray()
      ));
    return faults;
  }

  async download(mydocmaUid: string, faults: Fault[], openMessageCenter: boolean = false): Promise<Fault[]> {
    if (this.connectivityService.isOfflineVal) {
      throw new Error(`Can't download fault in offline`);
    }

    if (!(faults?.length > 0)) {
      return;
    }

    if (faults.length === 1) {
      try {
        //Load fault from api
        await this.loadFaults(faults);
        await this.downloadFault(mydocmaUid, faults[0]);
      } catch (error) {
        console.error('Download of fault failed', faults[0], error);
        throw new Error((await firstValueFrom(this.translateService.get('MM.fault.downloadFailedFault'))));
      }
    }
    else {
      //Load fault from api

      const faultsToDownload = faults;
      let processedFaultsAmount = 0;
      this.downloadCanceled = false;

      if (this.blockingDialogService.isDialogOpen()) {
        this.blockingDialogService.setProgressText(null);
        this.blockingDialogService.setShowCancel(true);
        this.blockingDialogService.setRecordText(await firstValueFrom(this.translateService.get('MM.syncFaults.syncBlockingDialog.recordsText')));
        this.blockingDialogService.appBlockedDialogRef.afterClosed().subscribe(didUserCancel => {
          if (didUserCancel) {
            this.cancelDownload();
          }
        });
      }
      else {
        this.blockingDialogService.show(await firstValueFrom(this.translateService.get('MM.downloadFaults.downloadDialog.inProgressHeader')),
          await firstValueFrom(this.translateService.get('MM.downloadFaults.downloadDialog.inProgressBody')),
          faultsToDownload.length,
          true,
          null,
          await firstValueFrom(this.translateService.get('MM.downloadFaults.downloadDialog.records'))).subscribe(didUserCancel => { if (didUserCancel) { this.cancelDownload(); } });
      }
      try {
        //Load all faults from api
        await this.loadFaults(faults);
      } catch (error) {
        console.error('Faul downloading from api failed', error);
        throw new Error((await firstValueFrom(this.translateService.get('MM.fault.downloadFailedFault'))));
      }


      const backgroundJob = new AttachmentDownloadJob(mydocmaUid);
      backgroundJob.openMessageCenter = openMessageCenter;

      for (const fault of faults) {
        processedFaultsAmount++;
        if (this.downloadCanceled) { break; }
        try {
          await this.downloadFault(mydocmaUid, fault, backgroundJob);
        } catch (error) {
          console.error('Download of fault failed', fault, error);
          this.notificationService.error(await firstValueFrom(this.translateService.get('MM.fault.downloadFailedFault')));
        }
        this.blockingDialogService.setProcessedFiles(processedFaultsAmount);
      }
      if (!this.downloadCanceled && this.platformModeStateService.canDownloadAttachments && backgroundJob.attachments.size > 0) {
        await this.attachmentDownloadService.startBackgroundDownload(backgroundJob);
      }
    }
    this.blockingDialogService.close();

    return faults;
  }

  async removeDownload(mydocmaUid: string, f: Fault): Promise<Fault> {

    if (!f.downloaded) {
      return f;
    }
    if (f.hasAnyChanges) {
      const copy = new Fault(f);
      copy.attachments.forEach(a => {
        if (a.changeState.status !== ChangeStatusEnum.New) {
          a.source.loadState = new LoadState({ status: LoadStatusEnum.NotLoaded });
        }
      });
      copy.activities.forEach(ac =>
        ac.attachments.forEach(a => {
          if (a.changeState.status !== ChangeStatusEnum.New) {
            a.source.loadState = new LoadState({ status: LoadStatusEnum.NotLoaded });
          }
        })
      );
      copy.downloaded = false;
      f.downloaded = false;
      await this.persistenceService.saveOrRemoveIfNotNeeded(mydocmaUid, copy);
    } else {
      f.downloaded = false;
      await this.persistenceService.saveOrRemoveIfNotNeeded(mydocmaUid, f);
    }
    this.faultDownloadClearedSubject.next(f);

    return f;
  }

  private async downloadAttachment(faultUid:string, a: Attachment, mydocmaUid: string, activityUid?: string, backgroundJob?: AttachmentDownloadJob): Promise<Attachment> {
    if (backgroundJob) {
      backgroundJob.addAttachment(faultUid, a, mydocmaUid, activityUid);
    } else {
      await this.attachmentDownloadService.downloadAttachment(faultUid, a, mydocmaUid, activityUid);
    }
    return a;
  }

  private async downloadFault(mydocmaUid: string, f: Fault, backgroundJob?: AttachmentDownloadJob): Promise<Fault> {

    for (const a of f.attachments) {
      try {
        if(f.downloaded && await this.offlineService.isAttachmentDownloaded(a.uid)){
          a.loadState.status = LoadStatusEnum.Loaded;
          continue;
        }
        if(this.platformModeStateService.canDownloadAttachments){
          await this.downloadAttachment(f.uid, a, mydocmaUid, null, backgroundJob);
          a.loadState.status = LoadStatusEnum.Loading;
        }else{
          a.loadState.status = LoadStatusEnum.NotLoaded;
        }
      } catch (error) {
        console.error('Download of attachment failed', a, error);
        this.notificationService.error(await firstValueFrom(this.translateService.get('MM.attachment.errors.downloadFile')));
      }
    }
    for (const ac of f.activities) {
       for (const a of (ac.attachments || [])) {
          try {
            if(f.downloaded && await this.offlineService.isAttachmentDownloaded(a.uid)){
              a.loadState.status = LoadStatusEnum.Loaded;
              continue;
            }

            if(this.platformModeStateService.canDownloadAttachments){
              await this.downloadAttachment(f.uid, a, mydocmaUid, ac.uid, backgroundJob);
              a.loadState.status = LoadStatusEnum.Loading;
            }else{
              a.loadState.status = LoadStatusEnum.NotLoaded;
            }
          } catch (error) {
            console.error('Download of activity attachment failed', a, error);
          }
        }
    }

    f.downloaded = true;
    await this.persistenceService.save(mydocmaUid, f, true);
    this.faultDownloadedSubject.next(f);
    return f;
  }

  async downloadNotLoadedFaultAttachments(mydocmaUid: string, faults: Fault[]): Promise<void> {
    if(faults?.length == 1){
      await this.downloadAttachmentsForFault(mydocmaUid, faults[0]);
      return;
    }
    const backgroundJob = new AttachmentDownloadJob(mydocmaUid);
    backgroundJob.openMessageCenter = true;
    for(const fault of faults) {
      await this.downloadAttachmentsForFault(mydocmaUid, fault, backgroundJob);
    }
    if(backgroundJob.attachments.size > 0) {
      await this.attachmentDownloadService.startBackgroundDownload(backgroundJob);
    }
  }

  async downloadAttachmentsForFault(mydocmaUid: string, f: Fault, backgroundJob?: AttachmentDownloadJob): Promise<void> {
    for (const a of f.attachments) {
      try {
        if(await this.offlineService.isAttachmentDownloaded(a.uid)){
          a.loadState.status = LoadStatusEnum.Loaded;
          continue;
        }
        await this.downloadAttachment(f.uid, a, mydocmaUid, null, backgroundJob);
        a.loadState.status = LoadStatusEnum.Loading;
      } catch (error) {
        console.error('Download of attachment failed', a, error);
        this.notificationService.error(await firstValueFrom(this.translateService.get('MM.attachment.errors.downloadFile')));
      }
    }
    for (const ac of f.activities) {
       for (const a of (ac.attachments || [])) {
          try {
            if(await this.offlineService.isAttachmentDownloaded(a.uid)){
              a.loadState.status = LoadStatusEnum.Loaded;
              continue;
            }
            await this.downloadAttachment(f.uid, a, mydocmaUid, ac.uid, backgroundJob);
            a.loadState.status = LoadStatusEnum.Loading;
          } catch (error) {
            console.error('Download of activity attachment failed', a, error);
          }
        }
    }
  }

  async remove(mydocmaUid: string, fault: Fault): Promise<any> {
    if (!fault.downloaded) {
      return;
    }

    await this.persistenceService.removeAll(mydocmaUid, [fault?.uid], null, null);
  }

  async removeAll(faults: Fault[]): Promise<any> {
  }

  public cancelDownload() {
    this.downloadCanceled = true;
    this.onDownloadCanceled.next(true);

  }
}
